import { useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { useDispatch } from "react-redux";

import { durationFilterDSSelected } from "app/actions/usersFiltersSelectionActions";
import { swSettings } from "app/common/services/swSettings";
import DurationSelectorSimple from "app/components/duration-selector/DurationSelectorSimple";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { getLastMonthDuration, getLastWeekDuration } from "../../../helpers/dateHelpers";
import { generateAutomationName, getMinMaxDate } from "../../../helpers/helpers";
import { BenchmarkGranularity } from "../../../services/api/constants";
import { benchmarkTrackerTracking } from "../../../services/tracking";
import type { BenchmarkUrlParams } from "../../../types/paramsTypes";

import { CONFIG_ID } from "./constants";
import { useDurationChangedToastMessage } from "./useDurationChangedToastMessage";

type DurationSchema = { height?: number; customOnChange?: (date: string) => void };

const Duration = ({ height, customOnChange }: DurationSchema) => {
    const dispatch = useDispatch();
    const { state, params } = useCurrentStateAndParams<BenchmarkUrlParams>();
    const { skipFilterConsistency } = state;
    const { duration, granularity, isDurationChanged } = params;

    const { filters: trackFilter } = benchmarkTrackerTracking;

    const defaultWeeklyDuration = useMemo(() => getLastWeekDuration(), []);
    const defaultMonthlyDuration = useMemo(() => getLastMonthDuration(), []);
    const maxMinDates = useMemo(() => getMinMaxDate(), []);

    const [selectedGranularity, setSelectedGranularity] = useState<BenchmarkGranularity>(granularity);
    const [selectedDuration, setSelectedDuration] = useState<string>(duration || defaultWeeklyDuration);

    useDurationChangedToastMessage(isDurationChanged);

    const onDurationSelect = (currentDuration: string) => {
        dispatch(durationFilterDSSelected(currentDuration));
    };

    const onToggle = () => trackFilter.trackDateRangeToggle();

    const onSubmit = async (currentDuration: string) => {
        if (!swSettings.allowedDuration(currentDuration, undefined, state)) {
            return;
        }

        if (!skipFilterConsistency) {
            onDurationSelect(currentDuration);
        }
        if (customOnChange) {
            customOnChange(currentDuration);
        }

        trackFilter?.trackCountrySubmit();

        await proNavigator.updateParams({
            duration: currentDuration,
            granularity: selectedGranularity,
            isDurationChanged: null,
        });
    };

    const onGranularityToggleClick = (index: BenchmarkGranularity) => {
        const selectedGranularityByIndex = index ? BenchmarkGranularity.MONTHLY : BenchmarkGranularity.WEEKLY;
        const newDuration =
            selectedGranularityByIndex === BenchmarkGranularity.MONTHLY
                ? defaultMonthlyDuration
                : defaultWeeklyDuration;

        setSelectedGranularity(selectedGranularityByIndex);
        setSelectedDuration(newDuration);
    };

    return (
        <DurationSelectorSimple
            height={height}
            data-automation={generateAutomationName("report", ["topFiltersBar", "duration", "wrapper"])}
            key="filter-duration"
            isWeeklyDataAvailable
            minDate={maxMinDates.monthlyFrom}
            maxDate={maxMinDates.monthlyTo}
            minDailyDate={maxMinDates.weeklyFrom}
            maxDailyDate={maxMinDates.weeklyTo}
            selectedGranularity={selectedGranularity}
            isSingleMonthSelection
            showWeeklyToggle
            onGranularityToggleClick={onGranularityToggleClick}
            isDisabled={false}
            initialPreset={selectedDuration}
            lastWeeklyPreset={defaultWeeklyDuration}
            onSubmit={onSubmit}
            onToggle={onToggle}
            appendTo="body"
            keys=""
            compareAllowed={false}
            componentName={CONFIG_ID}
            withoutPresets
            placement="bottom-left"
            presentNotificationIndicator={isDurationChanged}
            presentNotificationTooltipKey="bt.report.filters.duration.duration-modified-tooltip.text"
        />
    );
};

export default Duration;
